import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {classNames} from '../support/classnames';
import {Sprint} from '../../models/sprint';
import {PresenterProps, withPresenter} from '../support/with_presenter';
import {SprintBlockPresenter} from './sprint_block_presenter';
import {Employee} from '../../models/employee';
import * as Modal from 'react-modal';
import {SprintUpdater} from '../../business/sprint_updater';
import {EstimateUpdater} from '../../business/estimate_updater';
import {HighlightState} from '../../business/sprint_highlighter';

interface OwnProps {
    sprint: Sprint;
    employee: Employee;
    compact: boolean;
}

const SprintBlockComponent: FunctionComponent<OwnProps & PresenterProps<SprintBlockPresenter>> = observer(
    ({presenter, sprint, compact, employee}) => {
        return (
            <>
                <a
                    draggable={true}
                    onDragStart={() => presenter.handleDragStart()}
                    onDragEnd={() => presenter.handleDragEnd()}
                    onClick={(e) => presenter.handleClick(e.ctrlKey, e.shiftKey)}
                    onMouseEnter={() => presenter.onMouseEnter()}
                    onMouseLeave={() => presenter.onMouseLeave()}
                    className={classNames('sprint', 'state-' + sprint.estimate.state, {
                        'sprint-extra': sprint.is_extra,
                        'sprint-internal': sprint.estimate.is_internal,
                        selected: presenter.selected,
                        'highlight-background': presenter.hightlightState === HighlightState.Background,
                        'highlight-highlighted': presenter.hightlightState === HighlightState.Highlighted,
                    })}>
                    <span className="text">{sprint.estimate.name}</span>
                    {!compact && <span className="hours">{sprint.hours}</span>}
                </a>
                {presenter.modalVisible && ( //This ensures the modal is only mounted when actually requested
                    <Modal
                        isOpen={presenter.modalVisible}
                        onRequestClose={() => presenter.onRequestModalClose()}
                        contentLabel={sprint.estimate.name}
                        ariaHideApp={false}
                        style={{
                            content: {
                                top: '50%',
                                left: '50%',
                                right: 'auto',
                                bottom: 'auto',
                                marginRight: '-50%',
                                transform: 'translate(-50%, -50%)',
                                width: '600px',
                            },
                        }}>
                        <div className="panel panel-default">
                            <div className="panel-heading">{sprint.estimate.name}</div>
                            <div className="panel-body panel-padding">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        presenter.onSubmit();
                                    }}>
                                    <div className="form-group">
                                        <label htmlFor="input-name" className="col-sm-2 control-label">
                                            Employee
                                        </label>
                                        <div className="col-sm-10">{employee.name}</div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="input-name" className="col-sm-2 control-label">
                                            Owner
                                        </label>
                                        <div className="col-sm-10">{sprint.estimate.owner.name}</div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="input-name" className="col-sm-2 control-label">
                                            Last edited by
                                        </label>
                                        <div className="col-sm-10">
                                            {presenter.lastEditBy === undefined ? 'n/a' : presenter.lastEditBy.name}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="input-state" className="col-sm-2 control-label">
                                            State
                                        </label>
                                        <div className="col-sm-10">
                                            <select
                                                className="form-control "
                                                id="input-state"
                                                name="state"
                                                value={presenter.estimateState}
                                                onChange={(e) => presenter.onEstimateStateChange(e.target.value)}>
                                                <option value="approved">approved</option>
                                                <option value="estimated">estimated</option>
                                                <option value="hopefully">hopefully</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="input-hours" className="col-sm-2 control-label">
                                            Hours
                                        </label>
                                        <div className="col-sm-10">
                                            <input
                                                className="form-control"
                                                id="input-hours"
                                                name="hours"
                                                type="number"
                                                value={presenter.hours}
                                                onChange={(e) => presenter.onHoursChange(parseInt(e.target.value, 10))}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="col-sm-2 control-label">Extra?</label>
                                        <div className="col-sm-10">
                                            <div className="checkbox">
                                                <input
                                                    id="input-is-extra"
                                                    checked={presenter.isExtra}
                                                    name="is_extra"
                                                    type="checkbox"
                                                    onChange={(e) => presenter.onIsExtraChange(e.target.checked)}
                                                />
                                                <label htmlFor="input-is-extra">Extra?</label>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <footer>
                                <a
                                    className="btn btn-inline btn-secondary ion-android-close pull-left"
                                    onClick={() => presenter.onRequestModalClose()}>
                                    Close
                                </a>
                                <a
                                    href={`/estimates/${sprint.estimate.id}/edit`}
                                    className="btn  btn-inline btn-secondary ion-chevron-right pull-left">
                                    Estimate
                                </a>
                                <button
                                    className="btn btn-default btn-inline ion-android-done pull-right"
                                    onClick={() => presenter.onSaveClick()}>
                                    {presenter.saving ? 'Saving...' : 'Save'}
                                </button>
                            </footer>
                        </div>
                    </Modal>
                )}
            </>
        );
    },
);

export const SprintBlock = withPresenter<SprintBlockPresenter, OwnProps>(
    (props, component) =>
        new SprintBlockPresenter(
            props.sprint.id,
            props.sprint.estimate.id,
            props.sprint.hours,
            props.sprint.is_extra,
            props.sprint.estimate.state,
            component.dragContext,
            new SprintUpdater(component.sprintApi, component.sprintsRepository),
            new EstimateUpdater(component.estimateApi, component.gridLoader),
            component.sprintSelectionManager,
            component.usersRepository,
            component.sprintsRepository,
            component.sprintHighlighter,
        ),
    SprintBlockComponent,
);
